/**
 * Pure SEO builders shared by the React app (via src/utils/structuredData.ts)
 * and by scripts/prerender.js at build time, so the JSON-LD and metadata that
 * crawlers see in the static HTML is generated by exactly the same code as the
 * markup React renders afterwards.
 *
 * Rules: only describe things that are visible on the site. No invented
 * ratings, review counts, certifications, nutrition values or identifiers.
 */

export const absoluteUrl = (cfg, pathOrUrl) => {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${cfg.siteUrl}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
};

/** "₹1,200/kg" -> "1200". Returns null when there is no usable number. */
export const parsePrice = (value) => {
  if (!value) return null;
  const match = String(value).replace(/,/g, "").match(/\d+(?:\.\d+)?/);
  return match ? match[0] : null;
};

/** Trim to a search-result friendly length without cutting a word in half. */
export const truncate = (text, max = 160) => {
  const clean = String(text).replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max - 1);
  return `${cut.slice(0, cut.lastIndexOf(" ")).replace(/[,;:.\-\s]+$/, "")}…`;
};

/**
 * Page title with the brand appended, unless the title already names the brand or the
 * result would be too long to show in a search result (keeps long article titles intact).
 */
export const fullTitle = (cfg, title) => {
  if (title.includes(cfg.siteName) || title.includes("Golden Harvest")) return title;
  const branded = `${title} | ${cfg.siteName}`;
  return branded.length <= 70 ? branded : title;
};

/** Local image for a variety, matched by name against src/data/mangoImages.json. */
export const mangoImagePath = (name, imageMap) => {
  if (!name) return null;
  const n = name.toLowerCase().trim();
  if (imageMap[n]) return imageMap[n];
  for (const [key, value] of Object.entries(imageMap)) {
    if (n.includes(key) || key.includes(n)) return value;
  }
  return null;
};

const orgId = (cfg) => `${absoluteUrl(cfg, "/")}#organization`;
const orgRef = (cfg) => ({ "@id": orgId(cfg) });

const postalAddress = (cfg) => ({
  "@type": "PostalAddress",
  streetAddress: cfg.business.streetAddress,
  addressLocality: cfg.business.locality,
  addressRegion: cfg.business.region,
  postalCode: cfg.business.postalCode,
  addressCountry: cfg.business.country,
});

export const organizationNode = (cfg, description, sameAs = []) => ({
  "@type": "Organization",
  "@id": orgId(cfg),
  name: cfg.siteName,
  url: absoluteUrl(cfg, "/"),
  logo: {
    "@type": "ImageObject",
    url: absoluteUrl(cfg, cfg.logoPath),
    width: 512,
    height: 512,
  },
  image: absoluteUrl(cfg, cfg.defaultImage.path),
  ...(description && { description }),
  foundingDate: cfg.business.foundingYear,
  address: postalAddress(cfg),
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: cfg.business.phone,
      email: cfg.business.email,
      contactType: "customer service",
      areaServed: "IN",
    },
  ],
  ...(sameAs.length > 0 && { sameAs }),
});

export const websiteNode = (cfg) => ({
  "@type": "WebSite",
  "@id": `${absoluteUrl(cfg, "/")}#website`,
  name: cfg.siteName,
  url: absoluteUrl(cfg, "/"),
  inLanguage: "en-IN",
  publisher: orgRef(cfg),
});

/** Contact page only: the farm location and the opening hours printed on that page. */
export const localBusinessNode = (cfg) => ({
  "@type": "LocalBusiness",
  "@id": `${absoluteUrl(cfg, "/contact")}#localbusiness`,
  name: cfg.siteName,
  url: absoluteUrl(cfg, "/"),
  image: absoluteUrl(cfg, cfg.defaultImage.path),
  telephone: cfg.business.phone,
  email: cfg.business.email,
  address: postalAddress(cfg),
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    opens: cfg.business.hours.opens,
    closes: cfg.business.hours.closes,
  },
  parentOrganization: orgRef(cfg),
});

export const breadcrumbNode = (cfg, items) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: absoluteUrl(cfg, item.url),
  })),
});

export const itemListNode = (cfg, items) => ({
  "@type": "ItemList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    url: absoluteUrl(cfg, item.url),
  })),
});

/**
 * Title + description for a product page. Built only from the product's own
 * Firestore fields so every product gets unique, accurate metadata.
 */
export const productMeta = (cfg, product) => {
  const perKg = parsePrice(product.price);
  const bits = [
    perKg && `₹${Number(perKg).toLocaleString("en-IN")}/kg.`,
    product.available ? "In stock, delivered across India." : "Currently out of stock.",
    product.description && product.description.trim(),
  ].filter(Boolean);
  return {
    title: `${product.name} Mango | ${cfg.siteName}`,
    description: truncate(
      `${product.name} mangoes from ${cfg.siteName} in Saharanpur. ${bits.join(" ")}`
    ),
  };
};

/** Title + description for a /mangoes-delivery-<city> landing page, from src/data/deliveryLocations.json. */
export const locationMeta = (cfg, loc) => ({
  title: `Mango Delivery in ${loc.city} | ${cfg.siteName}`,
  description: truncate(
    `Order fresh mangoes online from ${cfg.siteName} in Saharanpur and get them delivered to ${loc.city}. Estimated delivery: ${loc.deliveryTime}. Shipping: ${loc.shippingCharges}.`
  ),
});

export const productNode = (cfg, product, imageUrl) => {
  const url = absoluteUrl(cfg, `/mangoes/${product.id}`);
  const availability = product.available
    ? "https://schema.org/InStock"
    : "https://schema.org/OutOfStock";
  const seller = orgRef(cfg);

  const offers = [];
  const perKg = parsePrice(product.price);
  if (perKg) {
    offers.push({
      "@type": "Offer",
      url,
      priceCurrency: "INR",
      price: perKg,
      availability,
      seller,
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: perKg,
        priceCurrency: "INR",
        referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitCode: "KGM" },
      },
    });
  }
  for (const option of product.weightOptions || []) {
    const price = parsePrice(option.price);
    const weight = parsePrice(option.weight);
    if (!price || !weight) continue;
    offers.push({
      "@type": "Offer",
      name: `${weight} kg`,
      url,
      priceCurrency: "INR",
      price,
      availability,
      seller,
      eligibleQuantity: { "@type": "QuantitativeValue", value: Number(weight), unitCode: "KGM" },
    });
  }

  let offersNode;
  if (offers.length === 1) {
    offersNode = offers[0];
  } else if (offers.length > 1) {
    const prices = offers.map((o) => Number(o.price));
    offersNode = {
      "@type": "AggregateOffer",
      priceCurrency: "INR",
      lowPrice: String(Math.min(...prices)),
      highPrice: String(Math.max(...prices)),
      offerCount: offers.length,
      availability,
      offers,
    };
  }

  return {
    "@type": "Product",
    "@id": `${url}#product`,
    name: `${product.name} Mango`,
    url,
    ...(product.description && { description: product.description }),
    ...(imageUrl && { image: imageUrl }),
    sku: product.id,
    category: "Fresh Fruit > Mangoes",
    brand: { "@type": "Brand", name: cfg.siteName },
    ...(offersNode && { offers: offersNode }),
  };
};

export const blogPostingNode = (cfg, post) => {
  const url = absoluteUrl(cfg, `/blog/${post.slug}`);
  return {
    "@type": "BlogPosting",
    "@id": `${url}#article`,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    headline: post.title,
    description: post.description,
    datePublished: post.date,
    dateModified: post.date,
    image: absoluteUrl(cfg, post.image || cfg.defaultImage.path),
    author: orgRef(cfg),
    publisher: orgRef(cfg),
    inLanguage: "en-IN",
  };
};

export const blogNode = (cfg, posts) => ({
  "@type": "Blog",
  "@id": `${absoluteUrl(cfg, "/blog")}#blog`,
  name: `${cfg.siteName} Blog`,
  url: absoluteUrl(cfg, "/blog"),
  publisher: orgRef(cfg),
  blogPost: posts.map((post) => ({
    "@type": "BlogPosting",
    headline: post.title,
    url: absoluteUrl(cfg, `/blog/${post.slug}`),
    datePublished: post.date,
    image: absoluteUrl(cfg, post.image || cfg.defaultImage.path),
  })),
});

export const toGraph = (...nodes) => ({
  "@context": "https://schema.org",
  "@graph": nodes,
});
